import type { ContactJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'

export const MESSAGE_FIELDS = `id orgId workspaceId locationId threadId from to callerId sentByUserId isAgent aiWorkflowId aiWorkflowName isInitialMessage contactId channel subject htmlBody attachments { filename contentType sizeBytes } text read archived createdDate updatedDate status sendDate providerMessageId errorCode errorMessage`

// 2 clauses per key keeps a chunk under Lucene's 1024 max_clause_count.
export const OPT_OUT_KEYS_PER_QUERY = 50

export type ConversationType = MessageJson & {
  total: number
  unread: number
  hasAI?: boolean
  memberThreadIds?: string[]
  // Latest non-email message in the group: the source of the conversation's phone.
  latestText?: MessageJson
}

export type ResolvedInboxSelection =
  | {
      type: `select`
      threadId: string
      contactId?: string
      selectedContact?: ContactJson
    }
  | {
      type: `clear`
    }
  | {
      type: `noop`
    }

export type InboxLoaderData = {
  threadId: string
  locationId: string
  location?: LocationJson
  query: string
  filter: string
}

export type InboxState = {
  isLoading: boolean
  // True once SYNC_APPLIED has fired; gates the initial-fetch skeleton over the empty state.
  hasSynced: boolean
  pendingMutationCount: number
  selectedThreadId: string
  selectedContactId?: string
  selectedContact?: ContactJson
  conversations: ConversationType[]
  messages?: MessageJson[]
  unreadLocations?: Record<string, number>
  optimisticMessages?: MessageJson[]
  // Row-marked-unread holds (threadId -> count) that survive index lag until canonical agrees or the thread leaves.
  optimisticUnreadThreads: Record<string, number>
}

export type InboxAction =
  | { type: `SET_LOADING`; isLoading: boolean }
  | { type: `UPDATE_OPTIMISTIC_MESSAGES`; updater: (prev?: MessageJson[]) => MessageJson[] | undefined }
  | {
      type: `UPDATE_UNREAD_LOCATIONS`
      updater: (prev?: Record<string, number>) => Record<string, number> | undefined
    }
  | { type: `UPDATE_CONVERSATIONS`; updater: (prev: ConversationType[]) => ConversationType[] }
  | { type: `SELECT_CONVERSATION`; threadId: string; contactId?: string; contact?: ContactJson }
  | { type: `ADD_CONVERSATION`; contact: ContactJson; threadId?: string }
  | { type: `RESET_INBOX` }
  | { type: `BEGIN_MUTATION` }
  | { type: `END_MUTATION` }
  | {
      type: `SYNC_APPLIED`
      conversations: ConversationType[]
      unreadLocations: Record<string, number>
      messages: MessageJson[]
    }
  | { type: `SYNC_FAILED` }
  | { type: `SYNC_CLEARED` }
  | { type: `LOCATION_CHANGED` }
  | {
      type: `MARK_READ_OPTIMISTIC`
      updatedIds: string[]
      read: boolean
      unread: number
      locationId?: string
    }
  | {
      type: `SEND_MESSAGE_OPTIMISTIC`
      message: MessageJson
      currentThreadId?: string
      newlyReadCount: number
      locationId?: string
    }
  // `unread: 0` clears the hold, rolling back a refused write.
  | { type: `SET_THREAD_UNREAD_OPTIMISTIC`; threadId: string; unread: number; locationId?: string }
  | { type: `ARCHIVE_LOCAL_REMOVE`; threadId: string }
  | { type: `SET_RESOLVED_SELECTION`; threadId: string; contactId?: string; selectedContact?: ContactJson }
  | { type: `CLEAR_RESOLVED_SELECTION` }
